/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { FormattedMessage } from '@modules/i18n'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isAnnualAndOverages, isDrawdown } from '@modules/billing-lib/billingDetails/utils'

import ReviewChangesLink from './ReviewChangesLink'
import ChangeSubscriptionLink from './ChangeSubscriptionLink'

import type { Props } from './types'

interface FeatureUsageContentProps extends Props {
  onReviewChanges: () => void
  onChangeSubscription: () => void
}

const FeatureUsageContent = ({
  featureName,
  overrideUsageLevel,
  onReviewChanges,
  onChangeSubscription,
}: FeatureUsageContentProps) => {
  const billingDetails = useBillingDetails()

  const getChangeSubscriptionActionMessage = () => {
    if (isAnnualAndOverages(billingDetails.data!) || isDrawdown(billingDetails.data!)) {
      return overrideUsageLevel ? (
        <FormattedMessage
          data-test-id='annual-usage-notice-override-usage-level'
          id='annual-usage-notice.override-usage-level.message'
          defaultMessage='Contact your sales representative to upgrade your subscription.'
        />
      ) : (
        <FormattedMessage
          data-test-id='annual-usage-notice'
          id='annual-usage-notice.message'
          defaultMessage='Contact your sales representative to upgrade your subscription or remove these features.'
          values={{
            removeFeatures: (
              <ReviewChangesLink onReviewChanges={onReviewChanges}>
                <FormattedMessage
                  id='annual-usage-notices.message.review-changes'
                  defaultMessage='remove these features'
                />
              </ReviewChangesLink>
            ),
          }}
        />
      )
    }

    return overrideUsageLevel ? (
      <FormattedMessage
        data-test-id='monthly-usage-notice-override-usage-level'
        id='monthly-usage-notice.override-usage-level.message'
        defaultMessage='{changeSubscription}.'
        values={{
          changeSubscription: (
            <ChangeSubscriptionLink onChangeSubscription={onChangeSubscription} />
          ),
        }}
      />
    ) : (
      <FormattedMessage
        data-test-id='monthly-usage-notice'
        id='usage-notices.message'
        defaultMessage='{changeSubscription} or {reviewChanges}.'
        values={{
          changeSubscription: (
            <ChangeSubscriptionLink onChangeSubscription={onChangeSubscription} />
          ),
          reviewChanges: (
            <ReviewChangesLink onReviewChanges={onReviewChanges}>
              <FormattedMessage
                id='usage-notices.message.review-changes'
                defaultMessage='review required feature changes'
              />
            </ReviewChangesLink>
          ),
        }}
      />
    )
  }

  return (
    <FormattedMessage
      data-test-id='usage-notice.prefix'
      id='usage-notice.message-prefix'
      defaultMessage='Your subscription level doesn’t match {features} you’re using. {changeSubscriptionAction}'
      values={{
        features: overrideUsageLevel ? (
          <span>{featureName}</span>
        ) : (
          <FormattedMessage id='usage-notices.message.features' defaultMessage='the features' />
        ),
        changeSubscriptionAction: getChangeSubscriptionActionMessage(),
      }}
    />
  )
}

export default FeatureUsageContent
